import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WithAuth } from "../../components/with-auth";
import ChatInterface from '../../components/chat-interfact';
import MainWrapper from "../../layout/MainWrapper";
import { createAxiosInstance } from "../../api/ApiRequest";
import type { GetProp, TableProps } from 'antd';
import { Button, Input, Table, Modal } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { SearchOutlined, CheckCircleTwoTone, EditOutlined, MessageTwoTone } from "@ant-design/icons";
import { format } from 'date-fns';
import { notifyError } from "../../utils/toast";
import { useServerContext } from "../../context/ServerContext";
import { useAuth } from "../../context/AuthContext";

type ColumnsType<T extends object = object> = TableProps<T>['columns'];
type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>;

interface DataType {
    _id: String,
    index: Number,
    fullname: String,
    username: String,
    chatid: Number,
    lastcontent: String,
    lastasktime: Date | String,
    answered: Boolean,
    receiverTgId: String,
    receiverTgUsername: String,
    lastanswerTime: Date | String
}

interface TableParams {
    pagination?: TablePaginationConfig;
    sortField?: SorterResult<any>['field'];
    sortOrder?: SorterResult<any>['order'];
}

interface Message {
    id: number;
    text: string;
    sender: "user" | "admin";
    timestamp: number;
}

const getParams = (params: TableParams) => ({
    results: params.pagination?.pageSize,
    page: params.pagination?.current,
    ...params,
});

const AnswerQuestion: React.FC = () => {
    const history = useNavigate();
    const { logout } = useAuth();
    const axiosInstance = createAxiosInstance(history, logout);
    const { supportRefreshInterval } = useServerContext();
    const [search, setSearch] = useState('');
    const [editId, setEditId] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [userMessages, setUserMessages] = useState<Message[]>([]);
    const [data, setData] = useState<DataType[]>();
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 10,
        },
    });

    useEffect(() => {
        const timer = setInterval(() => {
            setRefresh((r) => !r);
        }, supportRefreshInterval * 1000);
        return () => clearInterval(timer);
    }, [supportRefreshInterval]);

    const highlight = (text: unknown) => {
        const value = text?.toString() ?? '';
        if (!search.trim()) return value;
        return (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[search.trim()]}
                autoEscape
                textToHighlight={value}
            />
        );
    };

    const columns: ColumnsType<DataType> = [
        {
            title: 'No',
            dataIndex: 'index',
            sorter: false,
            width: 60,
            align: 'center',
            render: highlight,
        },
        {
            title: 'User Name',
            dataIndex: 'fullname',
            sorter: false,
            width: 140,
            onHeaderCell: () => ({ className: 'support-col-user' }),
            onCell: () => ({ className: 'support-col-user' }),
            render: highlight,
        },
        {
            title: 'TG Username',
            dataIndex: 'username',
            width: 130,
            sorter: false,
            onHeaderCell: () => ({ className: 'support-col-user' }),
            onCell: () => ({ className: 'support-col-user' }),
            render: highlight,
        },
        {
            title: 'TG Id',
            dataIndex: 'chatid',
            width: 110,
            sorter: false,
            onHeaderCell: () => ({ className: 'support-col-user' }),
            onCell: () => ({ className: 'support-col-user' }),
            render: highlight,
        },
        {
            title: 'Last Message',
            dataIndex: 'lastcontent',
            width: 120,
            render: highlight,
        },
        {
            title: 'Asked At',
            dataIndex: 'lastasktime',
            width: 150,
            sorter: false,
            render: (item) => format(new Date(item), "yyyy-MM-dd HH:mm")
        },
        {
            title: 'Status',
            dataIndex: 'answered',
            width: 80,
            align: 'center',
            sorter: false,
            render: (item) => {
                if (item) return <CheckCircleTwoTone />;
                else return <MessageTwoTone twoToneColor="#f54444" />
            },
        },
        {
            title: 'Admin TG',
            dataIndex: 'receiverTgUsername',
            width: 130,
            sorter: false,
            onHeaderCell: () => ({ className: 'support-col-admin' }),
            onCell: () => ({ className: 'support-col-admin' }),
            render: highlight,
        },
        {
            title: 'Admin TG Id',
            dataIndex: 'receiverTgId',
            width: 120,
            sorter: false,
            onHeaderCell: () => ({ className: 'support-col-admin' }),
            onCell: () => ({ className: 'support-col-admin' }),
            render: highlight,
        },
        {
            title: 'Answered At',
            dataIndex: 'lastanswerTime',
            width: 150,
            sorter: false,
            render: (item) => item.length > 0 ? format(new Date(item), "yyyy-MM-dd HH:mm") : ""
        },
        {
            title: 'Action',
            dataIndex: 'chatid',
            width: 80,
            align: 'center',
            sorter: false,
            render: (item) => (
                <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => getMessages(item)}
                />
            )
        }
    ];

    const getMessages = async (id: number) => {
		setEditId(id);
        setLoading(true);
        let results = await axiosInstance.post('/api/v1/get_user_faq', { chatid: id });
        if (results.data && results.data.flag && results.data.flag == true) {
            setUserMessages(results.data.messages);
            setLoading(false);
            setModalOpen(true);
        } else {
            setLoading(false);
            notifyError(results.data.message);
        }
    }

    const fetchData = async () => {
        setLoading(true);
        let results = await axiosInstance.post('/api/v1/get_questions', {
            search: search.trim() || undefined,
            ...getParams(tableParams),
        })
        if (results.data && results.data.flag && results.data.flag == true) {
            setData(results.data.questions);
            setLoading(false);
            setTableParams({
                ...tableParams,
                pagination: {
                    ...tableParams.pagination,
                    total: results.data.totalNum,
                },
            });
        } else {
			notifyError(results.data.message);
			logout();
        }
    };

    useEffect(() => {
        fetchData();
    }, [
        tableParams.pagination?.current,
        tableParams.pagination?.pageSize,
        tableParams?.sortOrder,
        tableParams?.sortField,
        search,
        modalOpen,
        refresh
    ]);

    const handleTableChange: TableProps<DataType>['onChange'] = (pagination, _filters, sorter) => {
        setTableParams({
            pagination,
            sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
            sortField: Array.isArray(sorter) ? undefined : sorter.field,
        });

        if (pagination.pageSize !== tableParams.pagination?.pageSize) {
            setData([]);
        }
    };

    return (
        <WithAuth>
            <MainWrapper>
                <Input
                    placeholder="Search user, admin tg, tg id, message..."
                    prefix={<SearchOutlined />}
                    allowClear
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setTableParams((prev) => ({
                            ...prev,
                            pagination: { ...prev.pagination, current: 1 },
                        }));
                    }}
                    style={{ marginBottom: 16, maxWidth: 480 }}
                />
                <Table
                    className="dashboard-data-table support-questions-table"
                    columns={columns}
                    rowKey={(record) => record._id.toString()}
                    dataSource={data}
                    pagination={tableParams.pagination}
                    loading={loading}
                    onChange={handleTableChange}
                    bordered
                    size="middle"
                    scroll={{ x: 'max-content' }}
                />
            </MainWrapper>
            <Modal
                title="Chat History"
                centered
                open={modalOpen}
                onOk={() => setModalOpen(false)}
                onCancel={() => setModalOpen(false)}
                width={1500}
            >
                <ChatInterface key={JSON.stringify(userMessages)} newMessages={userMessages} userChatId={editId} />
            </Modal>
        </WithAuth>
    );
};

export default AnswerQuestion;
